import React from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet';
import { NeuralBackground } from '@/components/hero/NeuralBackground';
import { SynapticText } from '@/components/hero/SynapticText';
import { Brain, Clock, Languages, TrendingUp, Shield, Zap, CheckCircle, Play, ArrowRight, Mic, BarChart3, Users, FileText } from 'lucide-react';
import { Link } from 'react-router-dom';
import { MorphingGlassCard } from '@/components/cards/MorphingGlassCard';
import { JourneyCard } from '@/components/cards/JourneyCard';
import { FloatingIslandCard } from '@/components/cards/FloatingIslandCard';
import { HolographicCard } from '@/components/cards/HolographicCard';

const CognitiveScreeningIntake = () => {
  const [typingCardIndex, setTypingCardIndex] = React.useState(-1);
  const [completedCards, setCompletedCards] = React.useState<number[]>([]);
  const howItWorksSteps = [
    {
      icon: Play,
      title: "Patient Check-In",
      description: "Click 'Start Check-In' on phone, tablet, or desktop.",
      number: "01"
    },
    {
      icon: Mic,
      title: "Adaptive Interview",
      description: "Gia greets with empathy and asks dynamic questions based on prior responses.",
      number: "02"
    },
    {
      icon: Brain,
      title: "Biomarker Analysis",
      description: "Real-time extraction of tone, rhythm, fluency, and cognitive load.",
      number: "03"
    },
    {
      icon: BarChart3,
      title: "Results Dashboard",
      description: "Instant visualization of scores, sentiment trends, and emotional states.",
      number: "04"
    },
    {
      icon: FileText,
      title: "Clinician Report",
      description: "CPT-coded summary pushed to your EMR via FHIR/HL7.",
      number: "05"
    }
  ];

  const keyFeatures = [
    {
      icon: Clock,
      title: "Rapid Screening",
      description: "Full assessment in under 5 minutes, anywhere.",
      gradient: "from-primary/20 to-accent/20"
    },
    {
      icon: Brain,
      title: "Speech Biomarker Engine",
      description: "2,500+ signals for precise cognitive and emotional insight.",
      gradient: "from-accent/20 to-primary/20"
    },
    {
      icon: Languages,
      title: "Multilingual Support",
      description: "Conduct screenings in multiple languages with equal accuracy.",
      gradient: "from-primary/20 to-secondary/20"
    },
    {
      icon: TrendingUp,
      title: "Longitudinal Tracking",
      description: "Compare current results with historical data for trend analysis.",
      gradient: "from-secondary/20 to-accent/20"
    },
    {
      icon: Shield,
      title: "Secure & Compliant",
      description: "End-to-end encryption, HIPAA- and SOC 2-compliant.",
      gradient: "from-accent/20 to-primary/20"
    }
  ];

  const benefits = [
    "Scale Assessments: Screen every patient without adding staff.",
    "Early Detection: Identify cognitive decline months earlier than traditional methods.",
    "Workflow Integration: Zero disruption—Gia fits into existing clinical workflows."
  ];

  
  const handleCardComplete = (index: number) => {
    setCompletedCards(prev => [...prev, index]);
    if (index < howItWorksSteps.length - 1) {
      setTimeout(() => {
        setTypingCardIndex(index + 1);
      }, 300);
    }
  };

  React.useEffect(() => {
    // Start the journey when the section comes into view
    const timer = setTimeout(() => {
      setTypingCardIndex(0);
    }, 1000);
    return () => clearTimeout(timer);
  }, []);

  return (
    <>
      <Helmet>
        <title>Cognitive Screening & Intake - Complete Assessment in 5 Minutes | Scienza Health</title>
        <meta 
          name="description" 
          content="Gia's voice-to-voice AI conducts thorough cognitive assessments using 2,500+ speech biomarkers. Complete cognitive screening in under 5 minutes via any device." 
        />
      </Helmet>

      <div className="min-h-screen relative overflow-hidden" style={{ background: 'var(--gradient-depth)' }}>
        {/* Neural Background */}
        <NeuralBackground />
        
        {/* Hero Section */}
        <section className="relative z-10 pt-32 pb-20 px-4 sm:px-6 lg:px-8">
          <div className="max-w-6xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-center mb-16"
            >
              <div className="inline-flex items-center justify-center w-24 h-24 bg-primary/10 backdrop-blur-md border border-primary/20 rounded-full mb-8">
                <Brain className="w-12 h-12 text-primary" />
              </div>
              
              <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold text-white mb-6">
                Complete Cognitive Screening
                <span className="block text-transparent bg-clip-text bg-gradient-to-r from-primary to-accent mt-2">
                  in Under 5 Minutes
                </span>
              </h1>
              
              <p className="text-xl sm:text-2xl text-white/90 max-w-4xl mx-auto leading-relaxed mb-10">
                Gia's voice-to-voice AI engages naturally with patients—no apps or portals required.
              </p>

              <motion.a
                href="/contact-request-demo"
                className="inline-flex items-center px-12 py-4 bg-gradient-to-r from-primary to-accent text-white rounded-full font-semibold text-lg hover:scale-105 transition-all duration-300 shadow-lg hover:shadow-primary/25"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <Play className="w-5 h-5 mr-3" />
                See Gia in Action
              </motion.a>
            </motion.div>
          </div>
        </section>

        {/* Overview Section */}
        <section className="relative z-10 py-20 px-4 sm:px-6 lg:px-8">
          <div className="max-w-5xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="bg-background/10 backdrop-blur-md border border-primary/20 rounded-2xl p-8 md:p-12 relative overflow-hidden"
            >
              <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-accent/5"></div>
              <div className="relative z-10">
              <h2 className="text-3xl sm:text-4xl font-bold text-white mb-8 text-center">
                <SynapticText 
                  text="Digital Human® Cognitive Assessment" 
                  delay={300}
                />
              </h2>
              <SynapticText 
                text="Scienza Health's Cognitive Screening & Intake uses our Digital Human® agent, Gia, to conduct thorough cognitive assessments via any device. Leveraging 2,500+ speech biomarkers, Gia captures memory, attention, language, and emotional cues—flagging early signs of Alzheimer's, MCI, depression, anxiety, and PTSD in real time."
                delay={1200}
                className="text-lg text-white leading-relaxed"
              />
              </div>
            </motion.div>
          </div>
        </section>

        {/* How It Works Section */}
        <section className="relative z-10 py-20 px-4 sm:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-center mb-16"
            >
              <h2 className="text-4xl sm:text-5xl font-bold text-white mb-6">
                <SynapticText 
                  text="How It Works" 
                  delay={200}
                />
              </h2>
              <SynapticText 
                text="Follow Gia's journey from patient greeting to clinical insights"
                delay={1200}
                className="text-xl text-white max-w-3xl mx-auto"
              />
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-6">
              {howItWorksSteps.map((step, index) => (
                <JourneyCard
                  key={step.title}
                  icon={step.icon}
                  title={step.title}
                  description={step.description}
                  number={step.number}
                  index={index}
                  shouldStartTyping={index === typingCardIndex}
                  onComplete={() => handleCardComplete(index)}
                />
              ))}
            </div>
          </div>
        </section>

        {/* Key Features Section */}
        <section className="relative z-10 py-20 px-4 sm:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-center mb-16"
            >
              <h2 className="text-4xl sm:text-5xl font-bold text-white mb-6">
                <SynapticText 
                  text="Key Features" 
                  delay={200}
                />
              </h2>
              <SynapticText 
                text="Advanced capabilities for comprehensive cognitive assessment"
                delay={800}
                className="text-xl text-white max-w-3xl mx-auto"
              />
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10">
              {keyFeatures.slice(0, 3).map((feature, index) => (
                <motion.div
                  key={feature.title}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ 
                    duration: 0.6, 
                    delay: index * 0.15,
                    ease: "easeOut"
                  }}
                  viewport={{ once: true }}
                >
                  <FloatingIslandCard
                    icon={feature.icon}
                    title={feature.title}
                    description={feature.description}
                    gradient={feature.gradient}
                    index={index}
                  />
                </motion.div>
              ))}
            </div>
            
            {/* Second row with holographic cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-10 mt-12 max-w-4xl mx-auto">
              {keyFeatures.slice(3).map((feature, index) => (
                <motion.div
                  key={feature.title}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ 
                    duration: 0.6, 
                    delay: 0.45 + index * 0.15,
                    ease: "easeOut"
                  }}
                  viewport={{ once: true }}
                >
                  <HolographicCard
                    icon={feature.icon}
                    title={feature.title}
                    description={feature.description}
                    index={index + 3}
                  />
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* Benefits Section */}
        <section className="relative z-10 py-20 px-4 sm:px-6 lg:px-8">
          <div className="max-w-5xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-center mb-16"
            >
              <h2 className="text-4xl sm:text-5xl font-bold text-white mb-8">
                <SynapticText 
                  text="Clinical Benefits" 
                  delay={200}
                />
              </h2>
              
              <div className="bg-background/10 backdrop-blur-md border border-primary/20 rounded-2xl p-8 md:p-12 relative overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-accent/5"></div>
                
                <div className="relative z-10 space-y-8">
                  {benefits.map((benefit, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, x: -30 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      transition={{ 
                        duration: 0.6, 
                        delay: 0.3 + index * 0.2,
                        ease: "easeOut"
                      }}
                      viewport={{ once: true }}
                      className="flex items-start space-x-4"
                    >
                      <div className="w-8 h-8 bg-gradient-to-r from-primary to-accent rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                        <CheckCircle className="w-4 h-4 text-white" />
                      </div>
                      <div className="text-lg sm:text-xl text-white leading-relaxed">
                        <SynapticText 
                          text={benefit}
                          delay={600 + index * 400}
                          className="text-lg sm:text-xl text-white leading-relaxed"
                        />
                      </div>
                    </motion.div>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="relative z-10 py-20 px-4 sm:px-6 lg:px-8">
          <div className="max-w-4xl mx-auto text-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              <h2 className="text-4xl sm:text-5xl font-bold text-white mb-6">
                Transform Your Screening Process
              </h2>
              <p className="text-xl text-white/80 mb-10 max-w-3xl mx-auto">
                Experience the power of AI-driven cognitive assessment with our Digital Human® technology. 
                Start identifying cognitive decline months earlier than traditional methods.
              </p>
              
              <motion.a
                href="/contact-request-demo"
                className="inline-flex items-center px-12 py-4 bg-gradient-to-r from-primary to-accent text-white rounded-full font-semibold text-lg hover:scale-105 transition-all duration-300 shadow-lg hover:shadow-primary/25"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <Brain className="w-5 h-5 mr-3" />
                Start Your Free Trial Today
              </motion.a>
            </motion.div>
          </div>
        </section>
      </div>
    </>
  );
};

export default CognitiveScreeningIntake;